import React, { useEffect, useState } from "react";
import styles from "./WorldMap.module.scss";
import useWorldMap from "./useWorldMap";
import WorldMapHandler from "./WorldMapHandler";
import { InteractiveMap, CountryTooltip } from "../../components/worldMap";
import { Modal, MemberCard } from "../../components/common";

export default function WorldMap() {
  const { stateWorldMap, setWorldMap } = useWorldMap();
  const handlers = WorldMapHandler(stateWorldMap, setWorldMap);

  const [tooltipPosition, setTooltipPosition] = useState(null);

  // Load members on component mount
  useEffect(() => {
    handlers.handleLoadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track mouse position for tooltip
  useEffect(() => {
    const handleMouseMove = (e) => {
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className={styles.Container}>
      <header className={styles.Header}>
        <h1 className={styles.Title}>ASEM Connector</h1>
        <p className={styles.Subtitle}>
          Explore our global network of members across the world
        </p>
      </header>

      <main className={styles.Main}>
        {stateWorldMap.loading && (
          <div className={styles.LoadingContainer}>
            <div className={styles.Spinner}></div>
            <p>Loading map data...</p>
          </div>
        )}

        {stateWorldMap.error && (
          <div className={styles.ErrorContainer}>
            <span className="material-icons">error_outline</span>
            <p>{stateWorldMap.error}</p>
            <button onClick={handlers.handleLoadMembers} className={styles.RetryButton}>
              Retry
            </button>
          </div>
        )}

        {!stateWorldMap.loading && !stateWorldMap.error && (
          <>
            <div className={styles.MapContainer}>
              <InteractiveMap
                highlightedCountries={stateWorldMap.highlightedCountries}
                onCountryClick={handlers.handleCountryClick}
                onCountryHover={handlers.handleCountryHover}
                center={stateWorldMap.mapCenter}
                zoom={stateWorldMap.mapZoom}
                onMoveEnd={(position) => handlers.handleCenterChange(position.coordinates)}
                onGeographiesLoad={handlers.handleGeographiesLoad}
              />
              
              {/* Zoom Controls */}
              <div className={styles.ZoomControls}>
                <button 
                  className={styles.ZoomButton}
                  onClick={handlers.handleZoomIn}
                  title="Zoom In"
                  aria-label="Zoom In"
                >
                  <span className={styles.ZoomIcon}>+</span>
                </button>
                <button 
                  className={styles.ZoomButton}
                  onClick={handlers.handleZoomOut}
                  title="Zoom Out"
                  aria-label="Zoom Out"
                >
                  <span className={styles.ZoomIcon}>−</span>
                </button>
                <button 
                  className={styles.ZoomButton}
                  onClick={handlers.handleFitToHighlighted}
                  title="Fit to Highlighted Countries"
                  aria-label="Fit to Highlighted Countries"
                >
                  <span className={styles.ZoomIcon}>⌂</span>
                </button>
              </div>
            </div>

            {stateWorldMap.hoveredCountry && tooltipPosition && (
              <CountryTooltip
                country={stateWorldMap.hoveredCountry}
                position={tooltipPosition}
                isHighlighted={stateWorldMap.highlightedCountries.includes(
                  stateWorldMap.hoveredCountry.id
                )}
              />
            )}

            <Modal
              isOpen={stateWorldMap.isModalOpen}
              onClose={handlers.handleModalClose}
              title={`Members in ${stateWorldMap.selectedCountryName || "Country"}`}
            >
              {stateWorldMap.selectedMembers.length > 0 ? (
                <div className={styles.MembersList}>
                  {stateWorldMap.selectedMembers.map((member) => (
                    <MemberCard key={member.id} member={member} />
                  ))}
                </div>
              ) : (
                <p className={styles.NoMembers}>No members found in this country.</p>
              )}
            </Modal>
          </>
        )}
      </main>

      {!stateWorldMap.loading && !stateWorldMap.error && (
        <footer className={styles.Footer}>
          <div className={styles.Legend}>
            <h4>Legend:</h4>
            <div className={styles.LegendItems}>
              <div className={styles.LegendItem}>
                <span className={`${styles.ColorBox} ${styles.Highlighted}`}></span>
                <span>Countries with members</span>
              </div>
              <div className={styles.LegendItem}>
                <span className={`${styles.ColorBox} ${styles.Regular}`}></span>
                <span>Other countries</span>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
